/**
 * Planella domain types (Contract v1). Pure data, no React, no I/O.
 * Conventions: ids are ULIDs, money is integer agorot, instants are UTC ISO strings
 * ("2026-10-31T07:00:00.000Z"), local calendar days are 'YYYY-MM-DD' in the business timezone.
 */

export type Id = string;
/** UTC instant, always produced by Date#toISOString (so string order equals time order). */
export type IsoUtc = string;
/** Local calendar day in the business timezone, 'YYYY-MM-DD'. */
export type DayKey = string;
/** Local wall-clock time of day, 'HH:MM' (24h). */
export type HHMM = string;
/** Integer agorot (1 NIS = 100 agorot). */
export type Agorot = number;
/** E.164 phone number, e.g. +972501234567. */
export type E164 = string;

export const BUSINESS_TZ = 'Asia/Jerusalem';
export const CONSENT_WORDING_VERSION = '2026-11-v2';

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
let lastTime = -1;
let lastRandom: number[] = [];

/** Builds a ULID from a millisecond timestamp and a [0,1) random source. */
export function ulid(timeMs: number, random: () => number): Id {
  let t = Math.floor(timeMs);
  let time = '';
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD.charAt(t % 32) + time;
    t = Math.floor(t / 32);
  }
  let rand = '';
  for (let i = 0; i < 16; i++) rand += CROCKFORD.charAt(Math.floor(random() * 32) % 32);
  return time + rand;
}

function cryptoRandom(): number {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return (buf[0] ?? 0) / 0x100000000;
}

/**
 * New unique, time-sortable id (ULID). Monotonic within one millisecond so ids created in a
 * tight loop still sort in creation order.
 */
export function newId(timeMs: number = Date.now()): Id {
  if (timeMs === lastTime && lastRandom.length === 16) {
    for (let i = 15; i >= 0; i--) {
      const v = (lastRandom[i] ?? 0) + 1;
      if (v < 32) { lastRandom[i] = v; break; }
      lastRandom[i] = 0;
    }
    return ulid(timeMs, () => 0).slice(0, 10) + lastRandom.map((n) => CROCKFORD.charAt(n)).join('');
  }
  const id = ulid(timeMs, cryptoRandom);
  lastTime = timeMs;
  lastRandom = id.slice(10).split('').map((c) => CROCKFORD.indexOf(c));
  return id;
}

// ---------------------------------------------------------------- enums

export const PROFESSIONS = ['nails', 'lashes', 'brows', 'barber', 'hair', 'cosmetics', 'skincare', 'hair_removal', 'makeup'] as const;
export type Profession = (typeof PROFESSIONS)[number];

export type PlanTier = 'trial' | 'free' | 'solo' | 'studio' | 'salon';
export type Locale = 'he' | 'en' | 'ar';

export const APPOINTMENT_STATUSES = ['awaiting_deposit', 'pending', 'confirmed', 'completed', 'cancelled_client', 'cancelled_owner', 'no_show'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];
export type AppointmentSource = 'owner' | 'link' | 'waitlist';
/** 'claimed': the client says they paid by Bit/PayBox; the owner has to confirm (or reject) before the slot is confirmed. */
export type DepositStatus = 'none' | 'required' | 'claimed' | 'paid' | 'refunded' | 'expired' | 'failed';
export type PaymentMethod = 'cash' | 'card' | 'bit' | 'paybox' | 'other';
/** Methods a client can pay a deposit with online. */
export type OnlineMethod = 'card' | 'bit' | 'paybox';
/** Bit / PayBox have no per-business API: the owner supplies a payment link and/or the phone number to pay. */
export interface LinkMethod { enabled: boolean; link?: string; phone?: string }
export interface PaymentMethods { cash: boolean; card: boolean; bit: LinkMethod; paybox: LinkMethod }
/** What was collected on an appointment (the balance paid at the venue, or whatever markPaid recorded). */
export interface AppointmentPayment { method?: PaymentMethod; paidAgorot?: Agorot; paidAt?: IsoUtc }
export type CancelReason = 'client' | 'owner' | 'deposit_timeout' | 'deposit_failed';

export type WaitlistStatus = 'waiting' | 'offered' | 'booked' | 'expired';
export type Daypart = 'morning' | 'noon' | 'evening' | 'any';
export type OfferStatus = 'open' | 'accepted' | 'taken' | 'expired' | 'withdrawn';

export type ConsentPurpose = 'reminders' | 'marketing';
export type ConsentSource = 'booking_page' | 'waitlist' | 'owner_entered' | 'manage_page' | 'inbound_stop' | 'import' | 'client_request';

export type MessageStatus = 'queued' | 'sent' | 'delivered' | 'failed' | 'suppressed' | 'cancelled';
export type SuppressionReason = 'no_consent' | 'quiet_hours_deferred' | 'allowance_exhausted' | 'plan_free_manual';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'refunded' | 'partially_refunded';

export type FeeRule =
  | { kind: 'none' }
  | { kind: 'fixed'; amountAgorot: Agorot }
  | { kind: 'percent'; percent: number }
  /** Keep the deposit that was paid. */
  | { kind: 'deposit' };

// ---------------------------------------------------------------- entities

export interface Entity {
  id: Id;
  createdAt: IsoUtc;
  updatedAt: IsoUtc;
}
export interface BusinessRow extends Entity {
  businessId: Id;
}

export interface Brand {
  /** One of the 6 colour presets keys or a hex used by the page. */
  color: string;
  logoUrl?: string;
  coverUrl?: string;
  /** Work photos for the public page strip (max 8, each <= 200 KB). Use addBrandPhoto / removeBrandPhoto / reorderBrandPhotos. */
  photos?: BrandPhoto[];
}
export interface BrandPhoto { id: Id; dataUrl: string; alt?: string; order: number }
/** A retired booking-page slug that still resolves to the business until `until`. */
export interface SlugAlias { slug: string; until: IsoUtc }
export interface Business extends BusinessRow {
  /** Equals id. */
  businessId: Id;
  slug: string;
  name: string;
  profession: Profession;
  tz: string;
  locale: Locale;
  brand: Brand;
  plan: PlanTier;
  /** Start of the 30-day full-feature trial. */
  trialStartedAt: IsoUtc;
  /** Extra messages bought as top-up (500 for NIS 25), added to the monthly allowance. */
  topUpMessages: number;
  about?: string;
  whatsapp?: E164;
  phone?: E164;
  address?: string;
  instagram?: string;
  /** Demo salon flag, used for banners. */
  isDemo?: boolean;
  /** Previous slugs kept for >= 90 days after a change (old links and QR codes keep working). */
  slugAliases?: SlugAlias[];
  /** Set by deleteOwnerAccount: the account is closed to clients and every row is hard-deleted at `scheduledFor`. Cleared by cancelOwnerDeletion. */
  deletion?: AccountDeletion;
}
export interface AccountDeletion {
  requestedAt: IsoUtc;
  /** Hard delete happens at or after this instant (requestedAt + DELETION_GRACE_DAYS). */
  scheduledFor: IsoUtc;
  /** Future appointments cancelled when the request was made (they stay cancelled if the deletion is cancelled). */
  cancelledAppointments: number;
  /** Clients who were sent the closure message. */
  notifiedClients: number;
}
export interface Owner extends BusinessRow {
  phone: E164;
  name: string;
}
export interface Staff extends BusinessRow {
  name: string;
  active: boolean;
  /** Design-system colour key (1..8) used for calendar colouring. */
  colorKey: number;
  sortOrder: number;
  phone?: E164;
}
export interface Service extends BusinessRow {
  name: string;
  category: string;
  durationMin: number;
  bufferMin: number;
  priceAgorot: Agorot;
  depositAgorot?: Agorot;
  /** Empty array = every staff member can perform it. */
  staffIds: Id[];
  active: boolean;
  sortOrder: number;
  description?: string;
}
export interface TimeRange { start: HHMM; end: HHMM }
export interface WeeklyHours extends BusinessRow {
  staffId: Id;
  /** 0 = Sunday ... 6 = Saturday. */
  weekday: number;
  /** Empty = day off. Several ranges express breaks. */
  ranges: TimeRange[];
}
export interface TimeOff extends BusinessRow {
  /** undefined = whole business. */
  staffId?: Id;
  start: IsoUtc;
  end: IsoUtc;
  reason: string;
}
export interface Client extends BusinessRow {
  name: string;
  phone: E164;
  /** "הערות עבודה": formula, shade. May appear in owner UI only. */
  workNotes: string;
  /** "רגישויות ובריאות": never in messages, hidden behind tap-to-reveal, excluded from exports unless chosen. */
  sensitiveNotes: string;
  tags: string[];
  /** Set by deleteClientData / eraseMyData: the row stays as an anonymous tombstone. */
  deletedAt?: IsoUtc;
}
export interface Appointment extends BusinessRow {
  clientId: Id;
  serviceId: Id;
  staffId: Id;
  start: IsoUtc;
  end: IsoUtc;
  /** end + service buffer: the staff member is unavailable until then. */
  blockedUntil: IsoUtc;
  status: AppointmentStatus;
  source: AppointmentSource;
  manageToken: string;
  priceAgorot: Agorot;
  depositAgorot: Agorot;
  depositStatus: DepositStatus;
  /** How the deposit is (to be) paid. Absent for services without a deposit. */
  depositMethod?: OnlineMethod;
  /** Set when the client taps "I paid" for a Bit/PayBox deposit. */
  depositClaimedAt?: IsoUtc;
  /** Collected at the venue / on completion (markPaid). The deposit is tracked separately by depositStatus/depositMethod. */
  payment?: AppointmentPayment;
  paymentId?: Id;
  /** While awaiting_deposit the slot is held until this instant. */
  holdExpiresAt?: IsoUtc;
  cancelledAt?: IsoUtc;
  cancelReason?: CancelReason;
  paidAt?: IsoUtc;
  paidAgorot?: Agorot;
  /** Fee owed after a late cancellation / no-show (owner collects, Planella holds no funds). */
  feeAgorot?: Agorot;
  /** Set when moved: previous start, for undo and messages. */
  previousStart?: IsoUtc;
  note?: string;
}
export interface WaitlistEntry extends BusinessRow {
  clientId: Id;
  serviceId: Id;
  staffId?: Id;
  /** Local days the client would accept. */
  days: DayKey[];
  daypart: Daypart;
  status: WaitlistStatus;
  appointmentId?: Id;
}
export interface WaitlistOffer extends BusinessRow {
  entryId: Id;
  clientId: Id;
  serviceId: Id;
  staffId: Id;
  slotStart: IsoUtc;
  slotEnd: IsoUtc;
  /** Groups all offers for the same slot: first accept wins. */
  slotKey: string;
  token: string;
  expiresAt: IsoUtc;
  status: OfferStatus;
  appointmentId?: Id;
}
export interface ConsentRecord extends BusinessRow {
  clientId: Id;
  channel: 'whatsapp';
  purpose: ConsentPurpose;
  wordingVersion: string;
  grantedAt: IsoUtc;
  revokedAt: IsoUtc | null;
  source: ConsentSource;
  revokedSource?: ConsentSource;
}
export interface Message extends BusinessRow {
  channel: 'whatsapp';
  template: string;
  to: E164;
  params: Record<string, string>;
  status: MessageStatus;
  /** Why it is suppressed, or (status queued) why it was deferred. */
  suppressionReason?: SuppressionReason;
  scheduledFor: IsoUtc;
  sentAt?: IsoUtc;
  deliveredAt?: IsoUtc;
  appointmentId?: Id;
  clientId?: Id;
  purpose: ConsentPurpose;
  /** Idempotency key: one live message per key. */
  dedupeKey: string;
  /** Rendered text (set by the messaging adapter). */
  text?: string;
  /** Sent by the owner through wa.me (Free plan manual queue). */
  manual?: boolean;
  mock: true;
}
export interface Payment extends BusinessRow {
  appointmentId: Id;
  amountAgorot: Agorot;
  refundedAgorot: Agorot;
  status: PaymentStatus;
  gateway: 'mock';
  expiresAt: IsoUtc;
  paidAt?: IsoUtc;
}
export interface Settings extends BusinessRow {
  /** Equals businessId. */
  id: Id;
  cancelWindowH: number;
  lateCancelFee: FeeRule;
  noShowFee: FeeRule;
  rescheduleWindowH: number;
  leadTimeMin: number;
  maxAdvanceDays: number;
  slotStepMin: number;
  reminderOffsetH: number;
  remindersEnabled: boolean;
  quietHours: QuietHours;
  approvalMode: 'auto' | 'manual';
  depositHoldMin: number;
  /** Optional on rows stored before this field existed: read through `paymentMethodsOf()`. */
  paymentMethods?: PaymentMethods;
}
export interface QuietHours {
  /** Daily send window. */
  start: HHMM;
  end: HHMM;
  /** No sends Friday 16:00 to Saturday 20:00. */
  blockShabbat: boolean;
}

export interface EntityMap {
  businesses: Business;
  owners: Owner;
  staff: Staff;
  services: Service;
  weeklyHours: WeeklyHours;
  timeOff: TimeOff;
  clients: Client;
  appointments: Appointment;
  waitlistEntries: WaitlistEntry;
  waitlistOffers: WaitlistOffer;
  consents: ConsentRecord;
  messages: Message;
  payments: Payment;
  settings: Settings;
}
export type EntityName = keyof EntityMap;
export const ENTITY_NAMES: readonly EntityName[] = [
  'businesses', 'owners', 'staff', 'services', 'weeklyHours', 'timeOff', 'clients', 'appointments',
  'waitlistEntries', 'waitlistOffers', 'consents', 'messages', 'payments', 'settings',
];

/** A concrete bookable slot. */
export interface Slot {
  start: IsoUtc;
  end: IsoUtc;
  staffId: Id;
}

export const DEFAULT_SETTINGS: Omit<Settings, 'id' | 'businessId' | 'createdAt' | 'updatedAt'> = {
  cancelWindowH: 24,
  lateCancelFee: { kind: 'none' },
  noShowFee: { kind: 'none' },
  rescheduleWindowH: 12,
  leadTimeMin: 60,
  maxAdvanceDays: 60,
  slotStepMin: 15,
  reminderOffsetH: 24,
  remindersEnabled: true,
  quietHours: { start: '08:00', end: '20:00', blockShabbat: true },
  approvalMode: 'auto',
  depositHoldMin: 15,
  paymentMethods: { cash: true, card: true, bit: { enabled: false }, paybox: { enabled: false } },
};

export const DEFAULT_PAYMENT_METHODS: PaymentMethods = { cash: true, card: true, bit: { enabled: false }, paybox: { enabled: false } };
export const paymentMethodsOf = (s: Pick<Settings, 'paymentMethods'>): PaymentMethods => s.paymentMethods ?? DEFAULT_PAYMENT_METHODS;

/** Result convention for use cases. */
export type Ok<T> = { ok: true; value: T };
export type Fail<R extends string> = { ok: false; reason: R; nextSlots?: Slot[] };
export type Result<T, R extends string = string> = Ok<T> | Fail<R>;
export function ok<T>(value: T): Ok<T> { return { ok: true, value }; }
export function fail<R extends string>(reason: R, extra?: { nextSlots?: Slot[] }): Fail<R> {
  return extra?.nextSlots ? { ok: false, reason, nextSlots: extra.nextSlots } : { ok: false, reason };
}
